import type {
  ColorProperty,
  ColorPropertiesListResponse,
  ColorPropertiesFilter,
  ColorPropertiesStats,
} from '@src/types';
import { execAdmin } from '../utils';

const gql = String.raw;

interface ColorPropertyGraphQL {
  id: number;
  partnerCode: string;
  code: string;
  display: string;
  hexCode?: string | null;
  url?: string | null;
  synonyms: string[];
  family: string[];
  createdAt: string;
  updatedAt: string;
}

interface GetColorPropertiesResponse {
  partner: {
    colorProperties: {
      edges: { node: ColorPropertyGraphQL; cursor: string }[];
      pageInfo: {
        hasNextPage: boolean;
        hasPreviousPage: boolean;
        startCursor: string | null;
        endCursor: string | null;
      };
      totalCount: number;
    };
  };
}

interface GetColorPropertiesVariables {
  partnerCode: string;
  search?: string;
  first: number;
  after: string | null;
}

const GET_COLOR_PROPERTIES_QUERY = gql`
  query getColorProperties(
    $partnerCode: String!
    $search: String
    $first: Int
    $after: String
  ) {
    partner(code: $partnerCode) {
      colorProperties(search: $search, first: $first, after: $after) {
        edges {
          node {
            id
            partnerCode
            code
            display
            hexCode
            url
            synonyms
            family
            createdAt
            updatedAt
          }
          cursor
        }
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        totalCount
      }
    }
  }
`;

export const getColorProperties = async ({
  partnerCode,
  filter = {},
  page = 1,
  perPage = 50,
}: {
  partnerCode: string;
  filter?: ColorPropertiesFilter;
  page?: number;
  perPage?: number;
}): Promise<ColorPropertiesListResponse> => {
  // The API is cursor-paginated (Relay-style); its cursors are opaque but are
  // actually base64("cursor:<offset>") under the hood, so a numeric page can
  // be translated into a cursor without an extra round trip. See
  // reware-api's lib/util/cursor.ts for the matching encode/decode.
  const after = page > 1 ? btoa(`cursor:${(page - 1) * perPage - 1}`) : null;

  const variables: GetColorPropertiesVariables = {
    partnerCode,
    search: filter.searchQuery,
    first: perPage,
    after,
  };

  const data = await execAdmin<GetColorPropertiesVariables, GetColorPropertiesResponse>(
    GET_COLOR_PROPERTIES_QUERY,
    { variables }
  );

  const { edges, totalCount } = data.partner.colorProperties;

  // Map GraphQL camelCase to frontend snake_case
  const colorProperties: ColorProperty[] = edges.map(({ node }) => ({
    id: node.id,
    partner_code: node.partnerCode,
    code: node.code,
    display: node.display,
    hex_code: node.hexCode,
    url: node.url,
    synonyms: node.synonyms,
    family: node.family,
    created_at: node.createdAt,
    updated_at: node.updatedAt,
  }));

  return {
    data: colorProperties,
    total: totalCount,
  };
};

/**
 * Computes completeness stats over a full set of color properties. Callers
 * must supply the complete (unpaginated) list — e.g. via fetching all pages —
 * since a page-scoped subset would understate missing* counts relative to
 * `total`.
 */
export function calculateColorPropertiesStats(properties: ColorProperty[]): ColorPropertiesStats {
  const total = properties.length;
  const missingHex = properties.filter((p) => !p.hex_code).length;
  const missingSwatch = properties.filter((p) => !p.url).length;
  const missingGroup = properties.filter((p) => !p.family || p.family.length === 0).length;
  const incomplete = properties.filter((p) => !p.hex_code || !p.family || p.family.length === 0).length;
  const completeness = total > 0 ? Math.round(((total - incomplete) / total) * 100) : 0;

  return {
    total,
    missingHex,
    missingSwatch,
    missingGroup,
    completeness,
  };
}
